import { useState, useEffect, useCallback } from "react";
import {
    Send, Loader2, RefreshCw, Upload, AlertTriangle, FlaskConical, Info,
} from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import {
    META_CONVERSION_PENDING_URL, META_CONVERSION_SEND_URL, META_CONVERSION_LOGS_URL,
} from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { StatTile, money, num } from "./adReportKit";

const STATUS_TONE = {
    sent:    "text-emerald-600 dark:text-emerald-400",
    skipped: "text-muted-foreground",
    failed:  "text-red-600 dark:text-red-400",
    pending: "text-blue-600 dark:text-blue-400",
};

export default function AdConversionsReport() {
    const { fetch, create } = useApiMethods();

    const [pending, setPending] = useState(null);
    const [logs, setLogs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");
    const [tab, setTab] = useState("pending");

    const load = useCallback(() => {
        setLoading(true);
        fetch(META_CONVERSION_PENDING_URL)
            .then((res) => {
                if (res?.status === "success") setPending(res);
                else ToastMsg("error", res?.message || "Failed to load");
            })
            .catch(() => ToastMsg("error", "Failed to load"))
            .finally(() => setLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // status=sent only. Without it the log also returns dry-run rows (logged as
    // 'pending'), skipped and failed ones — so a tab labelled "sent" listed
    // leads that never left the building.
    const loadLogs = useCallback(() => {
        fetch(`${META_CONVERSION_LOGS_URL}?status=sent&limit=100`)
            .then((res) => { if (res?.status === "success") setLogs(res); })
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); loadLogs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const send = () => {
        setBusy("send");
        create(META_CONVERSION_SEND_URL, { dry_run: false, limit: 500 })
            .then((res) => {
                if (res?.status === "success") {
                    ToastMsg(res.result?.failed ? "error" : "success", res.message);
                    load(); loadLogs(); setTab("sent");
                } else ToastMsg("error", res?.message || "Send failed");
            })
            .catch((err) => ToastMsg("error",
                err?.response?.data?.message || "Send failed"))
            .finally(() => setBusy(""));
    };

    const s = pending?.summary;
    const rows = pending?.data || [];
    const pixel = pending?.pixel;
    const configured = pending?.configured;
    const ready = rows.filter((r) => r.ready);

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Upload className="h-6 w-6 text-primary" />
                            Conversions to Meta
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Which CRM outcomes are waiting to be reported back to Facebook, so its
                            ads optimise toward buyers instead of form-fillers.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => { load(); loadLogs(); }}
                            disabled={loading || !!busy}>
                            <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                            Refresh
                        </Button>
                        <Button size="sm" onClick={send}
                            disabled={!!busy || !configured || !ready.length}>
                            {busy === "send" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                                : <Send className="h-4 w-4 mr-1.5" />}
                            Send {ready.length ? `${ready.length} to Meta` : "to Meta"}
                        </Button>
                    </div>
                </div>

                {!loading && !configured && (
                    <div className="flex items-start gap-2 rounded-lg p-3 text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                            No pixel connected yet. Add one under
                            <strong className="mx-1">Settings → Meta Ads</strong>
                            and set which lead stage becomes which Meta event.
                        </span>
                    </div>
                )}

                {!loading && pending?.lead_id_only && (
                    <div className="flex items-start gap-2 rounded-lg p-3 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300">
                        <Info className="h-4 w-4 mt-0.5 shrink-0" />
                        <span>
                            Showing only leads that carry Meta's own lead id. That is an exact
                            match against Meta's record of the form submission, so nothing is
                            guessed and no personal data is sent — but it leaves out every lead
                            whose ad ids never reached the CRM. Widen it by passing the
                            campaign, adset and ad ids on the Zapier webhook.
                        </span>
                    </div>
                )}

                {pixel && (
                    <div className={cn(
                        "flex items-start gap-2 rounded-lg p-2.5 text-[11px]",
                        pixel.is_test
                            ? "bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300"
                            : "bg-muted/50 text-muted-foreground")}>
                        {pixel.is_test ? <FlaskConical className="h-3.5 w-3.5 mt-px shrink-0" />
                            : <Info className="h-3.5 w-3.5 mt-px shrink-0" />}
                        <span>
                            Sending to <strong>{pixel.name || pixel.id}</strong>
                            {pixel.is_test
                                ? " in test mode — events appear in Events Manager → Test Events and are excluded from optimisation."
                                : " live — events count toward optimisation and reporting."}
                            {pixel.last_sent_at && ` Last sent ${pixel.last_sent_at}.`}
                        </span>
                    </div>
                )}

                {configured && s && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        <StatTile tone="leads" label="Ready to send" value={num(s.ready)} />
                        <StatTile label="Skipped" value={num(s.skipped)}
                            sub="too old or unidentifiable" />
                        <StatTile tone="spend" label="Deal value" value={money(s.value_total)}
                            sub="on won deals in this batch" />
                        <StatTile label="Matched by lead id" value={num(s.by_lead_id)}
                            sub="exact match, no personal data" />
                        {!pending?.lead_id_only && (
                            <StatTile label="Matched by phone" value={num(s.by_phone)} />
                        )}
                    </div>
                )}

                {pending?.mappings?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {pending.mappings.map((m) => (
                            <span key={m.lead_stage}
                                className="text-[11px] px-2 py-1 rounded-md bg-muted text-muted-foreground">
                                {m.lead_stage} → <strong className="text-foreground">{m.event_name}</strong>
                                {m.send_value && " + value"}
                            </span>
                        ))}
                    </div>
                )}

                <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
                    {[["pending", `Waiting (${rows.length})`],
                      ["sent", `Sent to Meta (${logs?.match?.sent ?? 0})`]].map(([k, label]) => (
                        <button key={k} onClick={() => setTab(k)}
                            className={cn("px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                                tab === k ? "bg-background text-foreground shadow-sm ring-1 ring-border"
                                    : "text-muted-foreground hover:text-foreground")}>
                            {label}
                        </button>
                    ))}
                </div>

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && tab === "pending" && (
                    <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="w-full text-xs">
                            <thead className="bg-muted/50">
                                <tr className="text-muted-foreground">
                                    <th className="px-3 py-2.5 text-left font-medium">Lead</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Outcome → Event</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Matched on</th>
                                    <th className="px-3 py-2.5 text-right font-medium">Value</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Reached</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Will send?</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((r) => (
                                    <tr key={`${r.customer_id}-${r.lead_stage}`}
                                        className={cn("hover:bg-accent/30", !r.ready && "opacity-60")}>
                                        <td className="px-3 py-2 text-foreground truncate max-w-[190px]">
                                            {r.customer_name}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground">
                                            {r.lead_stage} → <span className="text-foreground font-medium">{r.event_name}</span>
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground">
                                            {r.matched_on?.length ? r.matched_on.join(", ") : "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">
                                            {r.value != null ? money(r.value) : "—"}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                                            {r.event_time || "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                            {r.ready ? (
                                                <span className="text-emerald-600 dark:text-emerald-400 font-medium">yes</span>
                                            ) : (
                                                <span className="text-muted-foreground">
                                                    no — {r.skip_reason}
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {!rows.length && (
                                    <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                                        {configured
                                            ? "Nothing waiting. Every mapped outcome has already been reported."
                                            : "Connect a pixel first."}
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && tab === "sent" && (
                    <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="w-full text-xs">
                            <thead className="bg-muted/50">
                                <tr className="text-muted-foreground">
                                    <th className="px-3 py-2.5 text-left font-medium">Lead</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Event</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Matched on</th>
                                    <th className="px-3 py-2.5 text-right font-medium">Value</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Sent</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Status</th>
                                    <th className="px-3 py-2.5 text-left font-medium">Sent by</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {(logs?.data || []).map((row) => (
                                    <tr key={row.id} className="hover:bg-accent/30">
                                        <td className="px-3 py-2 text-foreground truncate max-w-[180px]">
                                            {row.customer_name || `#${row.customer_id}`}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground">
                                            {row.lead_stage} → <span className="text-foreground">{row.event_name}</span>
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground">
                                            {row.matched_on?.length ? row.matched_on.join(", ") : "—"}
                                        </td>
                                        <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">
                                            {row.value != null ? money(row.value) : "—"}
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                                            {row.sent_at || "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className={cn("font-medium", STATUS_TONE[row.status])}>
                                                {row.status}{row.is_test && " (test)"}
                                            </span>
                                            {row.skip_reason && (
                                                <p className="text-[10px] text-muted-foreground">{row.skip_reason}</p>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {row.sent_by?.type === "system" ? (
                                                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
                                                    {row.sent_by.label}
                                                </span>
                                            ) : (
                                                <span className="text-foreground">{row.sent_by?.label || "—"}</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {!logs?.data?.length && (
                                    <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">
                                        Nothing has been sent to Meta yet.
                                    </td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* The log holds more than sends. Saying so stops a dry-run row
                    count from reading as "we already sent these". */}
                {!loading && tab === "sent" && !!logs?.summary && (
                    (logs.summary.pending || logs.summary.skipped || logs.summary.failed) ? (
                        <p className="text-[11px] text-muted-foreground">
                            Also in the log, not sent:
                            {logs.summary.pending ? ` ${logs.summary.pending} from a dry run` : ""}
                            {logs.summary.skipped ? `, ${logs.summary.skipped} skipped` : ""}
                            {logs.summary.failed ? `, ${logs.summary.failed} failed` : ""}
                            . A dry run records what it would have done without contacting Meta.
                        </p>
                    ) : null
                )}

                <div className="flex items-start gap-2 text-[11px] text-muted-foreground">
                    <Info className="h-3.5 w-3.5 mt-px shrink-0" />
                    <span>
                        Only leads carrying Meta's lead id are listed, and the list is exactly
                        what the button sends — nothing hidden here can be sent. Nothing is sent
                        automatically either; pressing the button is the only way events
                        reach Meta. Personal data never leaves the CRM: email and phone are
                        normalised and SHA-256 hashed, and a lead carrying Meta's own lead id needs
                        no personal data at all. Each outcome is sent once, so pressing Send twice
                        cannot double-count a sale.
                    </span>
                </div>
            </div>
        </ContainerCard>
    );
}
